import pluralize from "pluralize"
import PropTypes from "prop-types"
import React, { useEffect, useMemo, useRef } from "react"
import { useQuery } from "react-query"

import { getImportConfig } from "src/api/DromoImports"

import { useToast } from "src/hooks/use_toast"

import DromoImporter from "../DromoImports/DromoImporter"
import { getInventoryQuantityAdjustmentConfig } from "../DromoImports/configs/inventory_quantity_adjustment"

const IMPORT_TYPE = "inventory_quantity_adjustment"

const BulkQuantityAdjustmentImporter = ({
  selection,
  currentUserId,
  onClose,
}) => {
  const showToast = useToast()

  const { data: config, isError } = useQuery(
    ["importConfig", IMPORT_TYPE],
    () => getImportConfig(IMPORT_TYPE),
    { retry: false, refetchOnWindowFocus: false }
  )

  const { eligibleIds, skippedCount } = useMemo(() => {
    if (!config) return { eligibleIds: [], skippedCount: 0 }

    const valid = new Set(
      config.products.map((product) => String(product.value))
    )
    const eligible = selection.filter((id) => valid.has(String(id)))
    return {
      eligibleIds: eligible,
      skippedCount: selection.length - eligible.length,
    }
  }, [config, selection])

  const notifiedRef = useRef(false)
  useEffect(() => {
    if (notifiedRef.current) return

    if (isError) {
      notifiedRef.current = true
      showToast("Couldn't load inventory items. Please try again.", {
        type: "error",
      })
      onClose()
      return
    }

    if (!config) return
    notifiedRef.current = true

    if (eligibleIds.length === 0) {
      showToast(
        "None of the selected items can be adjusted. Only standard inventory items are supported.",
        { type: "error" }
      )
      onClose()
    } else if (skippedCount > 0) {
      showToast(
        `${skippedCount} ${pluralize(
          "item",
          skippedCount
        )} skipped (only standard inventory items can be adjusted)`,
        { type: "info" }
      )
    }
  }, [isError, config, eligibleIds, skippedCount, showToast, onClose])

  if (!config || eligibleIds.length === 0) return null

  const { fields, rowHooks, stepHooks } =
    getInventoryQuantityAdjustmentConfig(config)

  return (
    <DromoImporter
      importType={IMPORT_TYPE}
      fields={fields}
      rowHooks={rowHooks}
      stepHooks={stepHooks}
      initialData={eligibleIds.map((id) => ({ product_id: id }))}
      manualInputOnly
      currentUserId={currentUserId}
      onClose={onClose}
    />
  )
}

BulkQuantityAdjustmentImporter.propTypes = {
  selection: PropTypes.array.isRequired,
  currentUserId: PropTypes.string,
  onClose: PropTypes.func.isRequired,
}

export default BulkQuantityAdjustmentImporter
